import { useState, useEffect, useCallback, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { CheckCircle2, XCircle, ArrowRight, Trophy, Target } from 'lucide-react'
import { useProgress } from '../context/ProgressContext'
import { useAuth } from '../context/AuthContext'
import { useNavigate } from 'react-router-dom'
import { doc, getDoc, setDoc } from 'firebase/firestore'
import { db, APP_COLLECTION, APP_DOC } from '../lib/firebase'
import { getModule } from '../data/modules'
import { courseHref } from '../data/courses'

const ATTEMPT_SCORES = [1, 0.5, 0.25]
const MAX_ATTEMPTS = 3
const DEFAULT_PICK_COUNT = 5
const LS_KEY = (moduleId) => `quiz-state-${moduleId}`

/* ─── Shuffle helpers ─── */

function seededRandom(seed) {
  let s = seed
  return () => {
    s = (s * 1664525 + 1013904223) & 0xffffffff
    return (s >>> 0) / 0xffffffff
  }
}

function shuffleArray(arr, rng) {
  const out = [...arr]
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1))
    ;[out[i], out[j]] = [out[j], out[i]]
  }
  return out
}

/**
 * Pick `count` questions from the pool, shuffle their option order,
 * and remap correctIndex accordingly. Uses a seed so the same attempt
 * always produces the same selection (for state restoration).
 */
function prepareQuestions(pool, count, seed) {
  const rng = seededRandom(seed)
  const picked = shuffleArray(pool, rng).slice(0, count)

  return picked.map((q) => {
    // Build index array [0,1,2,3], shuffle it
    const indices = q.options.map((_, i) => i)
    const shuffled = shuffleArray(indices, rng)
    return {
      question: q.question,
      explanation: q.explanation,
      options: shuffled.map((i) => q.options[i]),
      correctIndex: shuffled.indexOf(q.correctIndex),
      context: q.context || null,
      _originalQuestion: q.question, // for result tracking
    }
  })
}

/* ─── Local storage helpers ─── */

function loadFromLocalStorage(moduleId) {
  try {
    const raw = localStorage.getItem(LS_KEY(moduleId))
    if (raw) return JSON.parse(raw)
  } catch {}
  return null
}

function saveToLocalStorage(moduleId, state) {
  try {
    if (state === null) { localStorage.removeItem(LS_KEY(moduleId)); return }
    localStorage.setItem(LS_KEY(moduleId), JSON.stringify(state))
  } catch {}
}

/* ─── Quiz component ─── */

export default function Quiz({
  moduleId,
  questions: questionPool = [],
  pickCount,
  passingScore = 0.7,
}) {
  const { completeModule, startModule, getReissuedModules } = useProgress()
  const { user } = useAuth()
  const navigate = useNavigate()

  const count = pickCount || Math.min(DEFAULT_PICK_COUNT, questionPool.length)

  // Load initial state synchronously from localStorage
  const initialState = useRef(loadFromLocalStorage(moduleId))
  const init = initialState.current

  // Seed determines which questions are picked and how answers are shuffled.
  // On retry we generate a new seed to get different questions/order.
  const [seed, setSeed] = useState(init?.seed || Date.now())
  const [preparedQuestions, setPreparedQuestions] = useState(() =>
    init?.seed ? prepareQuestions(questionPool, count, init.seed) : prepareQuestions(questionPool, count, seed)
  )

  const [currentIndex, setCurrentIndex] = useState(init?.currentIndex || 0)
  const [selectedOption, setSelectedOption] = useState(null)
  const [attemptsThisQuestion, setAttemptsThisQuestion] = useState(init?.attemptsThisQuestion || 0)
  const [questionRevealed, setQuestionRevealed] = useState(false)
  const [questionResults, setQuestionResults] = useState(init?.questionResults || [])
  const [finished, setFinished] = useState(init?.finished || false)
  const [firestoreChecked, setFirestoreChecked] = useState(false)
  const [retryCount, setRetryCount] = useState(init?.retryCount || 0)
  const quizStartTimeRef = useRef(init?.quizStartTime || Date.now())

  // Viewport-aware timing
  const quizRef = useRef(null)
  const isVisibleRef = useRef(false)
  const questionStartRef = useRef(Date.now())
  const accumulatedTimeRef = useRef(0)

  // Quiz-level accumulated visible time (sum across all questions)
  const quizAccumulatedTimeRef = useRef(0)
  const quizVisibleStartRef = useRef(Date.now())

  // Set up IntersectionObserver for viewport tracking
  useEffect(() => {
    const el = quizRef.current
    if (!el) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        const now = Date.now()
        if (entry.isIntersecting) {
          // Became visible — start timing
          isVisibleRef.current = true
          questionStartRef.current = now
          quizVisibleStartRef.current = now
        } else {
          // Left viewport — accumulate elapsed time
          if (isVisibleRef.current) {
            accumulatedTimeRef.current += now - questionStartRef.current
            quizAccumulatedTimeRef.current += now - quizVisibleStartRef.current
          }
          isVisibleRef.current = false
        }
      },
      { threshold: 0.3 }
    )

    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  // Also pause on tab hidden
  useEffect(() => {
    function handleVisChange() {
      if (document.hidden) {
        if (isVisibleRef.current) {
          const now = Date.now()
          accumulatedTimeRef.current += now - questionStartRef.current
          quizAccumulatedTimeRef.current += now - quizVisibleStartRef.current
          isVisibleRef.current = false
        }
      } else {
        // Tab visible again — will resume on next intersection check,
        // but if the element is still in viewport, resume now
        if (quizRef.current) {
          const rect = quizRef.current.getBoundingClientRect()
          const inView = rect.top < window.innerHeight && rect.bottom > 0
          if (inView) {
            const now = Date.now()
            isVisibleRef.current = true
            questionStartRef.current = now
            quizVisibleStartRef.current = now
          }
        }
      }
    }
    document.addEventListener('visibilitychange', handleVisChange)
    return () => document.removeEventListener('visibilitychange', handleVisChange)
  }, [])

  // Reset per-question timer when question changes, check visibility
  useEffect(() => {
    accumulatedTimeRef.current = 0
    const now = Date.now()
    questionStartRef.current = now
    // Re-check viewport visibility — if the quiz is visible, resume timing
    if (quizRef.current) {
      const rect = quizRef.current.getBoundingClientRect()
      const inView = rect.top < window.innerHeight && rect.bottom > 0 && !document.hidden
      isVisibleRef.current = inView
      if (inView) {
        quizVisibleStartRef.current = now
      }
    }
  }, [currentIndex])

  function getElapsedMs() {
    let total = accumulatedTimeRef.current
    if (isVisibleRef.current) {
      total += Date.now() - questionStartRef.current
    }
    return total
  }

  function getQuizElapsedMs() {
    let total = quizAccumulatedTimeRef.current
    if (isVisibleRef.current) {
      total += Date.now() - quizVisibleStartRef.current
    }
    return total
  }

  // If the question we restored to was already answered, mark it revealed
  useEffect(() => {
    if (init) {
      const existingResult = init.questionResults?.[init.currentIndex]
      if (existingResult && (existingResult.correct || existingResult.attempts >= MAX_ATTEMPTS)) {
        setQuestionRevealed(true)
      }
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Also check Firestore for more recent state (in case localStorage is stale)
  useEffect(() => {
    if (firestoreChecked || !user?.uid) return

    async function checkFirestore() {
      try {
        const docRef = doc(db, APP_COLLECTION, APP_DOC, 'users', user.uid, 'progress', String(moduleId))
        const snap = await getDoc(docRef)
        if (snap.exists()) {
          const saved = snap.data()

          // Check if admin has reissued this module
          if (saved?.reissued) {
            const reissuedAt = saved.reissued?.toDate ? saved.reissued.toDate().getTime() : new Date(saved.reissued).getTime()
            const quizFinishedAt = saved?.quizResults?.completedAt ? new Date(saved.quizResults.completedAt).getTime() : 0
            if (reissuedAt > quizFinishedAt) {
              // Admin reissued after last completion — start fresh
              const newSeed = Date.now()
              const newQuestions = prepareQuestions(questionPool, count, newSeed)
              setSeed(newSeed)
              setPreparedQuestions(newQuestions)
              setCurrentIndex(0)
              setSelectedOption(null)
              setAttemptsThisQuestion(0)
              setQuestionRevealed(false)
              setQuestionResults([])
              setFinished(false)
              setRetryCount((saved?.quizResults?.retryCount || 0) + 1)
              accumulatedTimeRef.current = 0
              quizAccumulatedTimeRef.current = 0
              const now = Date.now()
              questionStartRef.current = now
              quizVisibleStartRef.current = now
              quizStartTimeRef.current = now
              saveToLocalStorage(moduleId, { seed: newSeed, questionResults: [], currentIndex: 0, attemptsThisQuestion: 0, finished: false, retryCount: (saved?.quizResults?.retryCount || 0) + 1, quizStartTime: now })
              setFirestoreChecked(true)
              return
            }
          }

          const qs = saved?.quizState
          if (!qs || !qs.questionResults || qs.questionResults.length === 0) {
            // Firestore has no quiz state (admin reset) — clear local and start fresh
            if (init?.questionResults?.length > 0) {
              const newSeed = Date.now()
              const newQuestions = prepareQuestions(questionPool, count, newSeed)
              setSeed(newSeed)
              setPreparedQuestions(newQuestions)
              setCurrentIndex(0)
              setSelectedOption(null)
              setAttemptsThisQuestion(0)
              setQuestionRevealed(false)
              setQuestionResults([])
              setFinished(false)
              setRetryCount(0)
              const now = Date.now()
              accumulatedTimeRef.current = 0
              quizAccumulatedTimeRef.current = 0
              questionStartRef.current = now
              quizVisibleStartRef.current = now
              quizStartTimeRef.current = now
              saveToLocalStorage(moduleId, null)
            }
          } else if (qs.questionResults?.length > (init?.questionResults?.length || 0)) {
            // Firestore has more progress — use it
            setQuestionResults(qs.questionResults)
            setCurrentIndex(qs.currentIndex || 0)
            setAttemptsThisQuestion(qs.attemptsThisQuestion || 0)
            setFinished(qs.finished || false)
            if (qs.seed && qs.seed !== seed) {
              setSeed(qs.seed)
              setPreparedQuestions(prepareQuestions(questionPool, count, qs.seed))
            }
            if (qs.retryCount != null) setRetryCount(qs.retryCount)
            if (qs.quizStartTime) quizStartTimeRef.current = qs.quizStartTime
            const existingResult = qs.questionResults?.[qs.currentIndex]
            if (existingResult && (existingResult.correct || existingResult.attempts >= MAX_ATTEMPTS)) {
              setQuestionRevealed(true)
            }
            saveToLocalStorage(moduleId, qs)
          }
        } else if (init?.questionResults?.length > 0) {
          // Document doesn't exist in Firestore but localStorage has data — admin deleted it
          const newSeed = Date.now()
          const newQuestions = prepareQuestions(questionPool, count, newSeed)
          setSeed(newSeed)
          setPreparedQuestions(newQuestions)
          setCurrentIndex(0)
          setSelectedOption(null)
          setAttemptsThisQuestion(0)
          setQuestionRevealed(false)
          setQuestionResults([])
          setFinished(false)
          setRetryCount(0)
          saveToLocalStorage(moduleId, null)
        }
      } catch (err) {
        console.error('[Quiz] Firestore restore failed:', err)
      }
      setFirestoreChecked(true)
    }

    checkFirestore()
  }, [moduleId, user?.uid, firestoreChecked, init, seed, questionPool, count])

  // Mark module as started (only once)
  const startedRef = useRef(false)
  useEffect(() => {
    if (!startedRef.current) {
      startedRef.current = true
      startModule(moduleId)
    }
  }, [moduleId, startModule])

  // Persist quiz state to both localStorage and Firestore
  const persistState = useCallback((results, curIdx, attempts, isFinished, currentSeed, currentRetryCount) => {
    const state = {
      questionResults: results,
      currentIndex: curIdx,
      attemptsThisQuestion: attempts,
      finished: isFinished,
      seed: currentSeed || seed,
      retryCount: currentRetryCount ?? retryCount,
      quizStartTime: quizStartTimeRef.current,
    }

    saveToLocalStorage(moduleId, state)

    if (user?.uid) {
      const docRef = doc(db, APP_COLLECTION, APP_DOC, 'users', user.uid, 'progress', String(moduleId))
      setDoc(docRef, { quizState: state }, { merge: true }).catch((err) => {
        console.error('[Quiz] Firestore persist failed:', err)
      })
    }
  }, [moduleId, user?.uid, seed, retryCount])

  const question = preparedQuestions[currentIndex]
  const total = preparedQuestions.length

  const handleAnswer = useCallback((optIdx) => {
    if (questionRevealed) return

    const isCorrect = optIdx === question.correctIndex
    const newAttempts = attemptsThisQuestion + 1
    setSelectedOption(optIdx)
    setAttemptsThisQuestion(newAttempts)

    if (isCorrect || newAttempts >= MAX_ATTEMPTS) {
      setQuestionRevealed(true)
      const score = isCorrect ? ATTEMPT_SCORES[newAttempts - 1] : 0
      const timeMs = getElapsedMs()
      const result = {
        questionIndex: currentIndex,
        attempts: newAttempts,
        correct: isCorrect,
        score,
        timeMs,
        selectedAnswers: [...(questionResults[currentIndex]?.selectedAnswers || []), optIdx],
        questionText: question.question,
        options: question.options,
        correctIndex: question.correctIndex,
      }
      const newResults = [...questionResults]
      newResults[currentIndex] = result
      setQuestionResults(newResults)
      persistState(newResults, currentIndex, newAttempts, false)
    } else {
      const partialResult = {
        questionIndex: currentIndex,
        attempts: newAttempts,
        correct: false,
        score: 0,
        selectedAnswers: [...(questionResults[currentIndex]?.selectedAnswers || []), optIdx],
        questionText: question.question,
        options: question.options,
        correctIndex: question.correctIndex,
      }
      const newResults = [...questionResults]
      newResults[currentIndex] = partialResult
      setQuestionResults(newResults)
      persistState(newResults, currentIndex, newAttempts, false)

      setTimeout(() => {
        setSelectedOption(null)
      }, 800)
    }
  }, [question, attemptsThisQuestion, questionRevealed, currentIndex, questionResults, persistState])

  const handleNext = useCallback(() => {
    if (currentIndex + 1 >= total) {
      setFinished(true)
      persistState(questionResults, currentIndex, attemptsThisQuestion, true)
    } else {
      const nextIdx = currentIndex + 1
      const existingResult = questionResults[nextIdx]
      setCurrentIndex(nextIdx)
      setSelectedOption(null)
      setAttemptsThisQuestion(existingResult?.attempts || 0)
      setQuestionRevealed(existingResult?.correct !== undefined && (existingResult?.correct || existingResult?.attempts >= MAX_ATTEMPTS))
      persistState(questionResults, nextIdx, existingResult?.attempts || 0, false)
    }
  }, [currentIndex, total, questionResults, attemptsThisQuestion, persistState])

  const handleComplete = useCallback(() => {
    const totalScore = questionResults.reduce((sum, r) => sum + (r?.score || 0), 0)
    const maxScore = total
    const pct = totalScore / maxScore

    if (user?.uid) {
      const totalQuizTimeMs = getQuizElapsedMs()
      const attemptRecord = {
        attempt: retryCount + 1,
        questions: questionResults,
        totalScore,
        maxPossibleScore: maxScore,
        percentageScore: pct,
        totalQuizTimeMs,
        passed: pct >= passingScore,
        completedAt: new Date().toISOString(),
      }
      const docRef = doc(db, APP_COLLECTION, APP_DOC, 'users', user.uid, 'progress', String(moduleId))
      // Read existing attempts to append
      getDoc(docRef).then((snap) => {
        const existing = snap.exists() ? snap.data() : {}
        const history = existing.attemptHistory || []
        history.push(attemptRecord)
        return setDoc(docRef, {
          quizResults: {
            questions: questionResults,
            totalScore,
            maxPossibleScore: maxScore,
            percentageScore: pct,
            retryCount,
            totalQuizTimeMs,
            completedAt: attemptRecord.completedAt,
          },
          attemptHistory: history,
        }, { merge: true })
      }).catch(() => {})
    }

    // Always mark module as complete (finished), regardless of score
    completeModule(moduleId, pct)

    // Check if there are other reissued modules pending
    const pending = getReissuedModules().filter((id) => id !== moduleId)
    if (pending.length > 0) {
      navigate(`/module/${pending[0]}`)
    } else {
      // Back to the course this module belongs to, not the combined list.
      const track = getModule(moduleId)?.track
      navigate(track ? courseHref(track) : '/')
    }
  }, [questionResults, total, moduleId, user?.uid, completeModule, navigate, passingScore, retryCount, getReissuedModules])

  // Calculate scores
  const totalScore = questionResults.reduce((sum, r) => sum + (r?.score || 0), 0)
  const pct = total > 0 ? totalScore / total : 0
  const passed = pct >= passingScore

  // Finished screen
  if (finished) {
    return (
      <div className="space-y-6">
        <h2 className="text-2xl font-bold text-gray-900">Knowledge Check</h2>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-lg mx-auto text-center py-8"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 200, delay: 0.1 }}
            className={`w-20 h-20 rounded-full mx-auto flex items-center justify-center mb-4 ${
              passed ? 'bg-green-100' : 'bg-amber-100'
            }`}
          >
            {passed ? (
              <Trophy className="w-10 h-10 text-green-600" />
            ) : (
              <Target className="w-10 h-10 text-amber-600" />
            )}
          </motion.div>

          <h3 className="text-2xl font-bold text-gray-900 mb-2">
            {passed ? 'Great work!' : 'Module complete'}
          </h3>

          <p className="mb-6 text-gray-500">
            {passed
              ? 'You\'ve passed this module\'s knowledge check.'
              : 'You scored ' + Math.round(pct * 100) + '%. Review the material above to strengthen your understanding.'}
          </p>

          <div className="flex gap-3 justify-center">
            <button
              onClick={handleComplete}
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium transition-colors ${
                passed
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'bg-gray-800 text-white hover:bg-gray-700'
              }`}
            >
              Continue to Next Module
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </motion.div>
      </div>
    )
  }

  // Active quiz
  const remainingAttempts = MAX_ATTEMPTS - attemptsThisQuestion
  const showWrongFeedback = selectedOption !== null && selectedOption !== question.correctIndex && !questionRevealed

  return (
    <div ref={quizRef} className="space-y-6">
      <div>
        <h2 className="mb-2 text-2xl font-bold text-gray-900">Knowledge Check</h2>
        <p className="text-gray-600">
          {total} questions. Let&apos;s see what you&apos;ve learned.
        </p>
      </div>

      {/* Progress dots */}
      <div className="flex items-center justify-center gap-2">
        {preparedQuestions.map((_, idx) => {
          const r = questionResults[idx]
          let dotColor = 'bg-gray-200'
          if (r?.correct) dotColor = 'bg-green-500'
          else if (r && r.attempts >= MAX_ATTEMPTS && !r.correct) dotColor = 'bg-red-400'
          else if (idx === currentIndex) dotColor = 'bg-blue-500'
          return (
            <motion.div
              key={idx}
              className={`w-2.5 h-2.5 rounded-full transition-colors duration-200 ${dotColor}`}
              animate={idx === currentIndex ? { scale: [1, 1.3, 1] } : {}}
              transition={{ repeat: idx === currentIndex ? Infinity : 0, duration: 1.5 }}
            />
          )
        })}
      </div>

      {/* Question */}
      <AnimatePresence mode="wait">
        <motion.div
          key={`${seed}-${currentIndex}`}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.25 }}
          className="space-y-4"
        >
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-500">
              Question {currentIndex + 1} of {total}
            </span>
            {!questionRevealed && attemptsThisQuestion > 0 && (
              <span className="text-xs font-medium text-amber-600 bg-amber-50 px-2 py-1 rounded-full">
                {remainingAttempts} attempt{remainingAttempts !== 1 ? 's' : ''} remaining
              </span>
            )}
          </div>

          {question.context && (
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="w-full text-sm border-collapse">
                {question.context.headers && (
                  <thead>
                    <tr className="bg-gray-100">
                      {question.context.headers.map((h, i) => (
                        <th key={i} className="border-b border-r last:border-r-0 border-gray-200 px-3 py-1.5 text-left text-xs font-semibold text-gray-700">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                )}
                <tbody>
                  {question.context.rows.map((row, ri) => (
                    <tr key={ri} className={ri % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      {row.map((cell, ci) => (
                        <td key={ci} className="border-b border-r last:border-r-0 border-gray-200 px-3 py-1.5 text-xs text-gray-700">
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <h3 className="text-lg font-semibold text-gray-800">{question.question}</h3>

          <div className="space-y-2">
            {question.options.map((opt, idx) => {
              let style = 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50 cursor-pointer'

              if (questionRevealed) {
                if (idx === question.correctIndex) {
                  style = 'border-emerald-400 bg-emerald-50 text-emerald-800 cursor-default'
                } else if (questionResults[currentIndex]?.selectedAnswers?.includes(idx)) {
                  style = 'border-red-300 bg-red-50 text-red-700 cursor-default'
                } else {
                  style = 'border-gray-200 bg-gray-50 text-gray-400 cursor-default'
                }
              } else if (showWrongFeedback && idx === selectedOption) {
                style = 'border-red-400 bg-red-50 text-red-800 cursor-default'
              } else if (questionResults[currentIndex]?.selectedAnswers?.includes(idx)) {
                style = 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
              }

              const alreadyTried = !questionRevealed && questionResults[currentIndex]?.selectedAnswers?.includes(idx)

              return (
                <button
                  key={idx}
                  onClick={() => !questionRevealed && !alreadyTried && handleAnswer(idx)}
                  disabled={questionRevealed || alreadyTried}
                  className={`w-full rounded-lg border-2 px-4 py-3 text-left transition-colors ${style}`}
                >
                  <div className="flex items-center gap-3">
                    <span className="mr-1 inline-block w-6 text-center font-mono text-sm font-bold opacity-50">
                      {String.fromCharCode(65 + idx)}
                    </span>
                    <span className="text-sm flex-1">{opt}</span>
                    {questionRevealed && idx === question.correctIndex && (
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
                    )}
                    {questionRevealed && questionResults[currentIndex]?.selectedAnswers?.includes(idx) && idx !== question.correctIndex && (
                      <XCircle className="w-5 h-5 text-red-400 shrink-0" />
                    )}
                  </div>
                </button>
              )
            })}
          </div>

          {/* Wrong attempt feedback */}
          {showWrongFeedback && (
            <motion.div
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-lg border border-amber-200 bg-amber-50 p-3"
            >
              <p className="text-sm text-amber-800">
                Not quite — try again! {remainingAttempts} attempt{remainingAttempts !== 1 ? 's' : ''} left.
              </p>
            </motion.div>
          )}

          {/* Explanation — shown when question is fully revealed */}
          {questionRevealed && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="rounded-lg border border-blue-200 bg-blue-50 p-4"
            >
              <p className="text-sm text-blue-800">
                <strong>Explanation:</strong> {question.explanation}
              </p>
            </motion.div>
          )}

          {/* Next button */}
          {questionRevealed && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex justify-end">
              <button
                onClick={handleNext}
                className="rounded-lg bg-blue-600 px-5 py-2.5 font-medium text-white shadow-sm transition-colors hover:bg-blue-700 inline-flex items-center gap-2"
              >
                {currentIndex < total - 1 ? 'Next Question' : 'See Results'}
                <ArrowRight className="w-4 h-4" />
              </button>
            </motion.div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  )
}
